import { isDefined, supportedLanguages } from '@common'
import { SCIDApplication, SCID_SHOP_ITEM_TIERS } from '@common/models/scid'
import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/legacy/image'
import Link from 'next/link'

import { IconButton } from '@/components/buttons/IconButton'
import CollapsibleContainer from '@/components/CollapsibleContainer'
import { useGameImages } from '@/components/games/images'
import { Help } from '@/components/Icons'
import { Tooltip } from '@/components/Tooltip'
import { Game, GAME_STATUS } from '@/fixtures/games'
import { shortSlugToGameNameKey } from '@/localization/utils'
import { useAppSelector } from '@/utils/useAppSelector'

import { GameProgress } from '../../components/games/GameProgress'
import { getPassProductCopyKey, imageTierSuffix } from '../../utils/itemTiers'
import { useLocale } from '../locale/useLocale'

import styles from './MyGames.module.scss'

type GameRowProps = { game: Game; scidGameApplication: SCIDApplication }

const GameRow: React.FC<GameRowProps> = ({ game, scidGameApplication }) => {
  const t = useTranslations()
  const inventory = useAppSelector((state) => state.user.inventory)
  const locale = useLocale()
  const account = scidGameApplication.account

  const accountConnected = Boolean(account)

  const { gameImage } = useGameImages(game.slug)

  const seasonPasses = inventory?.items.filter(
    (item) => item.applications.find((app) => app.split('-')[0] === game.internalId) && item.type === 'SEASON_PASS',
  )

  const seasonPassesByTier =
    seasonPasses?.reduce(
      (acc, item) => {
        const tier = item.tier && SCID_SHOP_ITEM_TIERS.includes(item.tier) ? item.tier : 'NA'
        acc[tier] = (acc[tier] ?? 0) + 1
        return acc
      },
      {} as Record<string, number>,
    ) ?? {}

  const showInventory = accountConnected && Object.keys(seasonPassesByTier).length > 0
  const tooltipDirection = supportedLanguages[locale].rightToLeft ? 'right' : 'left'

  return (
    <li
      className={cx(styles.GameRow, {
        [styles.InActive]: !accountConnected,
      })}
      data-cy={`my-game-${game.slug}`}
    >
      <div className={styles.GameRowBadge}>
        <Image alt={'game-badge'} src={gameImage('gameBadge')} height={72} width={72} />
      </div>
      <div className={styles.GameRowTitle}>{t(shortSlugToGameNameKey(game.shortSlug))}</div>
      <div className={styles.GameRowName}>{account?.name ?? account?.tag}</div>
      <div className={styles.GameRowInfo}>
        {accountConnected && <GameProgress application={scidGameApplication.application} account={account} />}
      </div>
      {showInventory && (
        <div className={styles.GameRowInventory}>
          <p className={styles.InventoryTitle}>{t('acc_con_inv')}</p>
          {SCID_SHOP_ITEM_TIERS.filter((tier) => tier in seasonPassesByTier)
            .map((tier) => {
              const copyKey = getPassProductCopyKey(tier, game.shortSlug)
              if (!copyKey) {
                return null
              }
              return (
                <div key={tier}>
                  <div className={styles.InventoryRow}>
                    <div className={styles.InventoryRowImage}>
                      <Image
                        src={gameImage(`passProductImage${imageTierSuffix(tier)}`)}
                        layout="fill"
                        objectFit="contain"
                        alt="item-image"
                        sizes="5vw"
                      />
                    </div>

                    <p className={styles.InventoryRowItem}>{`${t(copyKey)}:`}</p>
                    <p className={styles.InventoryRowQuantity}>{seasonPassesByTier[tier]}</p>
                  </div>
                </div>
              )
            })
            .filter(isDefined)}
        </div>
      )}
      <div className={styles.GameRowConnection}>{t('acc_con_2')}</div>
      {!accountConnected && (
        <div className={styles.GameRowCta}>
          <Tooltip
            position={tooltipDirection}
            icon={
              <IconButton className={styles.TooltipButton}>
                <Help />
              </IconButton>
            }
          >
            <>
              {t('acc_con_how')}&nbsp;
              <Link href="/help">{t('acc_con_how_btn')}</Link>
            </>
          </Tooltip>
        </div>
      )}
    </li>
  )
}

export const MyGamesContainer: React.FC<{ games: Game[] }> = (props) => {
  const applications = useAppSelector((state) => state.user?.profile?.applications ?? [])
  const t = useTranslations()

  if (!applications) {
    return null
  }

  // Some next.js error if props.games === undefined?
  const games = props.games ?? []

  const { active, inactive } = games
    .map((game) => ({
      game,
      scidGameApplication: applications.find((a) => game.slug === a.application),
    }))
    .filter<GameRowProps>((a): a is GameRowProps => Boolean(a.scidGameApplication))
    .filter((a) => a.game.status === GAME_STATUS.ENABLED)
    .reduce<{ active: GameRowProps[]; inactive: GameRowProps[] }>(
      (acc, cur) => {
        if (cur.scidGameApplication.account) {
          acc.active = acc.active.concat(cur)
        } else {
          acc.inactive = acc.inactive.concat(cur)
        }
        return acc
      },
      { active: [], inactive: [] },
    )

  return (
    <CollapsibleContainer className={styles.ContentContainer} title={t('acc_myg')} defaultOpen>
      <div>
        <ul className={styles.GameList}>
          {active.map((gameRow) => (
            <GameRow key={gameRow.scidGameApplication.application} {...gameRow} />
          ))}
          {inactive.map((gameRow) => (
            <GameRow key={gameRow.scidGameApplication.application} {...gameRow} />
          ))}
        </ul>
        <div className={styles.BottomNote}>
          <p>{t('acc_games_note')}</p>
        </div>
      </div>
    </CollapsibleContainer>
  )
}
